// @section 1 Atlas route manifest — single source of truth.
// One entry per <Route>. Optional `nav` metadata adds a Sidebar entry and
// optional `shortcutKey` wires the ⌘<digit> handler. Adding a route means
// adding one file under routes/ and one entry below — App.tsx and Sidebar.tsx
// stay untouched.

import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import MyDay from './MyDay';
import DailyOpsReview from './DailyOpsReview';
import Thread from './Thread';
import Crm from './Crm';
import Strategy from './Strategy';
import Erp from './Erp';
import Delivery from './Delivery';
import Pm from './Pm';
import TimeValue from './TimeValue';
import OpsPulse from './OpsPulse';
import MissionControl from './MissionControl';
import Initiatives from './Initiatives';
import Campaigns from './campaigns';
import Calls from './Calls';
import DealsPipeline from './DealsPipeline';
import AccountWorkspace from './AccountWorkspace';
import AccountsV5 from './AccountsV5';
import OpportunityDetail from './OpportunityDetail';
import Bd from './Bd';
import Targets from './Targets';
import CardCapture from './CardCapture';
import Hopper from './Hopper';
import Library from './Library';
import Booker from './Booker';
import Documents from './Documents';
import AcquisitionsShell from './acquisitions';
import VpgReplies from './VpgReplies';
import VpgThreads from './VpgThreads';
import ClientsShell from './clients';
import Command from './command';

// @subsection 1.1 Types

export type NavConfig = {
  /** Sidebar group label this entry belongs under. */
  group: string;
  /** Display label in the sidebar. */
  label: string;
  /** Single-glyph icon. */
  icon: string;
  /** NavLink end-match behavior (highlight only on exact path). */
  end?: boolean;
  /** Display string for the keyboard shortcut hint (e.g. "⌘1"). */
  shortcut?: string;
  /** Single character ("1"–"9") wired to the ⌘<digit> handler. */
  shortcutKey?: string;
  /** Optional badge content shown right-aligned. */
  badge?: string | number;
};

export type RouteDef = {
  /** React-Router path, including trailing `/*` for splat routes. */
  path: string;
  /** Pre-instantiated element rendered by <Route element=...>. */
  element: ReactElement;
  /** Omit for routes that should NOT appear in the sidebar. */
  nav?: NavConfig;
};

export type SidebarItem =
  | { kind: 'label'; label: string; topGap?: boolean }
  | {
      kind: 'link';
      to: string;
      label: string;
      icon: string;
      end?: boolean;
      shortcut?: string;
      badge?: string | number;
    };

// @subsection 1.2 Group metadata
// `topGap` here drives the vertical separator between sidebar groups —
// keeping it data-driven means re-ordering groups doesn't require Sidebar
// code edits.
const GROUP_META: Record<string, { topGap?: boolean }> = {
  Workspace: {},
  Telemetry: { topGap: true },
};

// @subsection 1.3 Routes
// Order in this array drives sidebar order. Routes WITHOUT `nav` are
// rendered but never appear in the sidebar (parametric children, redirects,
// legacy paths). Place them at the bottom to keep the nav-bearing block
// scannable.

export const ROUTES: RouteDef[] = [
  // --- Workspace group ---
  // VPG reply work sits at the very top: it is the queue Patrick is measured on and it is
  // worked many times a day, so it should never be a scroll away.
  {
    path: '/vpg-replies',
    element: <VpgReplies />,
    nav: { group: 'Workspace', label: 'Reply Review', icon: '✉' },
  },
  {
    // Browsing companion to Reply Review: every thread, any contact, any location, any
    // state. Kept separate so the work queue stays a queue and lookups do not widen it.
    path: '/vpg-threads',
    element: <VpgThreads />,
    nav: { group: 'Workspace', label: 'All Threads', icon: '☰' },
  },
  {
    // Interim personal cockpit: merges Daily Ops Review + Ops Pulse + Initiatives
    // into one module with sub-tabs.
    path: '/command/*',
    element: <Command />,
    nav: { group: 'Workspace', label: 'Command', icon: '⊕' },
  },
  {
    path: '/',
    element: <MyDay />,
    nav: { group: 'Workspace', label: 'My Day', icon: '◐', end: true, shortcut: '⌘1', shortcutKey: '1' },
  },
  {
    // Folded into the Command module (Today tab); route kept mounted (no nav)
    // so existing deep links still resolve.
    path: '/daily-ops-review',
    element: <DailyOpsReview />,
  },
  {
    path: '/pipeline',
    element: <AccountsV5 />,
    nav: { group: 'Workspace', label: 'Pipeline', icon: '◇', shortcut: '⌘2', shortcutKey: '2' },
  },
  {
    path: '/clients/*',
    element: <ClientsShell />,
    nav: { group: 'Workspace', label: 'Clients', icon: '⊙' },
  },
  {
    path: '/bd',
    element: <Bd />,
    nav: { group: 'Workspace', label: 'BD', icon: '◢', shortcut: '⌘3', shortcutKey: '3' },
  },
  {
    // BD target-firm capture (voice memo -> firm/vertical/business model). Shared team view.
    path: '/targets',
    element: <Targets />,
    nav: { group: 'Workspace', label: 'Targets', icon: '◎' },
  },
  {
    // Prospect-conveyor capture front end: photograph business cards at an event,
    // narrate a voice note, confirm into CRM, queue the research checklist.
    path: '/card-capture',
    element: <CardCapture />,
    nav: { group: 'Workspace', label: 'Card Capture', icon: '▣' },
  },
  {
    // "See the library" — link-out directory of every live demo.
    path: '/library',
    element: <Library />,
    nav: { group: 'Workspace', label: 'Demo Library', icon: '▦' },
  },
  {
    path: '/strategy/*',
    element: <Strategy />,
    nav: { group: 'Workspace', label: 'Strategy', icon: '▲', shortcut: '⌘4', shortcutKey: '4' },
  },
  {
    path: '/pm/*',
    element: <Pm />,
    nav: { group: 'Workspace', label: 'Projects', icon: '□', shortcut: '⌘5', shortcutKey: '5' },
  },
  {
    path: '/campaigns/*',
    element: <Campaigns />,
    nav: { group: 'Workspace', label: 'Campaigns', icon: '↗', end: true, shortcut: '⌘6', shortcutKey: '6' },
  },
  {
    path: '/calls',
    element: <Calls />,
    nav: { group: 'Workspace', label: 'Calls', icon: '☏' },
  },
  // Operator view of the scalable-os.com free-lead products. Its job is the
  // manual-sourcing queue: signups we told "we are building your list by hand"
  // are invisible without this screen.
  {
    path: '/hopper',
    element: <Hopper />,
    nav: { group: 'Workspace', label: 'Hopper', icon: '⬒' },
  },
  // /deals-pipeline is the CRM sales-pipeline kanban (Vertis selling consulting).
  // Hidden from sidebar 2026-06-03 — Acquisitions (Vertis Holdings buy-side) is
  // the active deal workflow. Route still mounts so old links resolve.
  { path: '/deals-pipeline', element: <DealsPipeline /> },
  {
    path: '/booker',
    element: <Booker />,
    nav: { group: 'Workspace', label: 'Booker', icon: '⌖' },
  },
  {
    path: '/documents',
    element: <Documents />,
    nav: { group: 'Workspace', label: 'Documents', icon: '✒' },
  },
  {
    path: '/acquisitions/*',
    element: <AcquisitionsShell />,
    nav: { group: 'Workspace', label: 'Acquisitions', icon: '◈', shortcut: '⌘9', shortcutKey: '9' },
  },

  // --- Telemetry group ---
  {
    // Vertis Mission Control: six operational telemetry planes (jobs, sessions,
    // drift, github, deploys, campaigns) over /api/hq/ops/*. Read-only + one
    // safe act-back (job acknowledgment).
    path: '/mission-control',
    element: <MissionControl />,
    nav: { group: 'Telemetry', label: 'Mission Control', icon: '◉' },
  },
  // Ops Pulse + Initiatives folded into the Command module (Pulse / Initiatives
  // tabs); routes kept mounted (no nav) so deep links + the in-app
  // "Edit register" link to /initiatives still resolve.
  {
    path: '/ops-pulse',
    element: <OpsPulse />,
  },
  {
    path: '/initiatives',
    element: <Initiatives />,
  },
  {
    path: '/time-value/*',
    element: <TimeValue />,
    nav: { group: 'Telemetry', label: 'Time Value', icon: '◌', shortcut: '⌘7', shortcutKey: '7' },
  },
  {
    path: '/erp/*',
    element: <Erp />,
    nav: { group: 'Telemetry', label: 'Financials', icon: '$', shortcut: '⌘8', shortcutKey: '8' },
  },

  // --- Non-sidebar routes (parametric children, redirects, legacy paths) ---
  { path: '/inbox/:messageId', element: <Thread /> },
  { path: '/accounts/:id/*', element: <AccountWorkspace /> },
  { path: '/opportunity/:dealId', element: <OpportunityDetail /> },
  { path: '/crm/*', element: <Crm /> },
  { path: '/delivery/*', element: <Delivery /> },
  { path: '/morning-metrics/*', element: <Navigate to="/campaigns" replace /> },
  // /accounts was renamed to /pipeline 2026-06-06 when the post-win Clients
  // module landed. Keep old bookmarks/links resolving cleanly.
  { path: '/accounts', element: <Navigate to="/pipeline" replace /> },
];

// @subsection 1.4 Derivations

/** Strip a trailing `/*` so a splat route maps to a clean NavLink target. */
function navTarget(path: string): string {
  return path.replace(/\/\*$/, '');
}

/**
 * Build the sidebar item list from ROUTES. Group labels are emitted on
 * group transitions in manifest order — so reordering ROUTES reorders the
 * sidebar without any Sidebar code changes.
 */
export function buildSidebarItems(): SidebarItem[] {
  const items: SidebarItem[] = [];
  let currentGroup: string | null = null;
  for (const r of ROUTES) {
    if (!r.nav) continue;
    if (r.nav.group !== currentGroup) {
      items.push({
        kind: 'label',
        label: r.nav.group,
        topGap: GROUP_META[r.nav.group]?.topGap,
      });
      currentGroup = r.nav.group;
    }
    items.push({
      kind: 'link',
      to: navTarget(r.path),
      label: r.nav.label,
      icon: r.nav.icon,
      end: r.nav.end,
      shortcut: r.nav.shortcut,
      badge: r.nav.badge,
    });
  }
  return items;
}

/**
 * Keyboard ⌘<digit> map. Built from any ROUTES entry with a `shortcutKey`.
 * Lookup is O(1) by digit character: `SHORTCUT_MAP[e.key]`.
 */
export const SHORTCUT_MAP: Record<string, string> = Object.fromEntries(
  ROUTES.filter((r) => r.nav?.shortcutKey).map((r) => [r.nav!.shortcutKey!, navTarget(r.path)]),
);
